import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { useTranslation } from '../../i18n';
import { colors, primaryButtonSx, radius } from '../../theme/tokens';
import { splitCurrency } from '../../utils/currency';
import type { Plan } from '../../types/api';

/**
 * Cartão de um plano na lista.
 *
 * Paridade visual e de conteúdo com o bloco `@foreach($planos as $plano)` de
 * `resources/views/planos_lista.blade.php`: rótulo "Plano", selo de rede,
 * nome, preço quebrado em parte inteira/centavos, detalhamento e benefícios.
 */

interface PlanCardProps {
  plan: Plan;
  onSelect: (plan: Plan) => void;
  /** Posição na grade. O segundo cartão ganha a faixa de destaque. */
  index?: number;
}

/**
 * Posição (base zero) que recebe a faixa de destaque no topo do cartão.
 * Na view Blade era `@if($loop->iteration == 2)`, ou seja, o segundo plano.
 */
const HIGHLIGHT_INDEX = 1;

export function PlanCard({ plan, onSelect, index }: PlanCardProps) {
  const { t } = useTranslation();
  const { integer, cents } = splitCurrency(plan.valor);

  /**
   * O ERP nem sempre devolve `beneficios`. Nesse caso o legado caía para um
   * único item com `ligacoes` — mantido para não esvaziar o cartão em planos
   * antigos. Itens vazios são descartados (a limpeza de "Internet sem cortes"
   * em `sanitizePlan` pode deixar string vazia).
   */
  const benefits = Array.isArray(plan.beneficios)
    ? plan.beneficios.filter((item) => String(item).trim().length > 0)
    : [plan.ligacoes ?? ''].filter((item) => item.trim().length > 0);

  const description = plan.detalhamento?.trim();

  const isHighlighted = index === HIGHLIGHT_INDEX;

  return (
    <Box
      component="article"
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: colors.surface,
        border: `1px solid ${colors.borderSoft}`,
        borderRadius: radius.lg,
        overflow: 'hidden',
        boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
        transition: 'box-shadow 300ms ease, transform 300ms ease',
        '&:hover': {
          boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
          transform: 'translateY(-4px)',
        },
      }}
    >
      {isHighlighted ? (
        // Faixa de destaque do segundo plano, em gradiente da cor da marca.
        // Puramente decorativa: nada é anunciado a leitor de tela.
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 6,
            background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`,
          }}
        />
      ) : null}

      <Box sx={{ p: 4, flexGrow: 1 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 2,
            mb: 3,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              component="h3"
              sx={{
                fontSize: '0.8125rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                color: colors.textSecondary,
              }}
            >
              {t.plans.planLabel}
            </Typography>
            <Typography
              sx={{
                mt: 0.5,
                fontSize: '2.25rem',
                fontWeight: 800,
                lineHeight: 1.1,
                letterSpacing: '-0.02em',
                color: colors.textPrimary,
                wordBreak: 'break-word',
              }}
            >
              {plan.nome_plano}
            </Typography>
          </Box>

          <Box
            sx={{
              flexShrink: 0,
              backgroundColor: colors.successSoft,
              color: colors.success,
              px: 1.5,
              py: 0.5,
              borderRadius: radius.full,
              fontSize: '0.75rem',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            {t.plans.networkBadge}
          </Box>
        </Box>

        <Box sx={{ mb: 2, pb: 2, borderBottom: `1px solid ${colors.borderSoft}` }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', color: colors.textPrimary }}>
            <Typography component="span" sx={{ fontSize: '1.5rem', fontWeight: 700 }}>
              {t.plans.currency}
            </Typography>
            <Typography
              component="span"
              sx={{ ml: 0.5, fontSize: '3rem', fontWeight: 800, letterSpacing: '-0.02em' }}
            >
              {integer}
            </Typography>
            <Typography
              component="span"
              sx={{ fontSize: '1.5rem', fontWeight: 700, color: colors.textSecondary }}
            >
              {`,${cents}`}
            </Typography>
            <Typography
              component="span"
              sx={{ ml: 1, fontWeight: 500, color: colors.textMuted }}
            >
              {t.plans.perMonth}
            </Typography>
          </Box>
        </Box>

        {description ? (
          <Typography
            sx={{ mb: 3, fontSize: '0.875rem', lineHeight: 1.6, color: colors.textSecondary }}
          >
            {description}
          </Typography>
        ) : null}

        {benefits.length > 0 ? (
          <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 2 }}>
            {benefits.map((benefit, index) => (
              <Box
                component="li"
                // O ERP devolve benefícios repetidos em alguns planos, então o
                // texto sozinho não é chave estável.
                key={`${benefit}-${index}`}
                sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}
              >
                <Box
                  aria-hidden
                  sx={{
                    flexShrink: 0,
                    mt: '2px',
                    height: 24,
                    width: 24,
                    borderRadius: radius.full,
                    backgroundColor: colors.successSoft,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CheckRoundedIcon sx={{ fontSize: 16, color: colors.success }} />
                </Box>
                <Typography sx={{ fontWeight: 500, color: colors.textSecondary }}>
                  {benefit}
                </Typography>
              </Box>
            ))}
          </Box>
        ) : null}
      </Box>

      <Box
        sx={{
          p: 4,
          backgroundColor: colors.surfaceMuted,
          borderTop: `1px solid ${colors.borderSoft}`,
        }}
      >
        <Button
          type="button"
          fullWidth
          variant="contained"
          onClick={() => onSelect(plan)}
          endIcon={<ArrowForwardIcon />}
          sx={{ ...primaryButtonSx, py: 1.75, fontSize: { xs: '1rem', md: '1.125rem' } }}
        >
          {t.plans.select}
        </Button>
      </Box>
    </Box>
  );
}
